import { Box, Divider, Menu, MenuItem } from '@mui/material';
import React, { useEffect, useState } from 'react';
import StyledMain from '../../dashboard/styled/StyledMain';
import { Typography } from 'src/components/new/Typography';
import { Button } from 'src/components/new/Button';
import { Spacer } from 'src/components/new/Spacer';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import BkTable from 'src/components/table/BkTable';
import { Column } from 'src/utils/constants/table-constants';
import { LabeledCheckbox } from 'src/components/new/inputs/Checkbox';
import BkToolTip from 'src/components/tooltip/BkToolTip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import {
  deactivateMember,
  demoteMember,
  getAdminMembers,
  inviteNewUser,
  promoteMember,
  updateAdminConfig,
} from 'src/features/admin/utils';
import { AppDispatch, RootState } from 'src/store/store';
import AddNewAdminUser from './components/AddNewAdminUser';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { setBannerStatus, setCurrentUser } from 'src/features/user/userSlice';
import { getActiveBanner, refreshToken } from 'src/features/user/utils';

interface SnackbarProps {
  type: 'success' | 'error';
  msg: string;
}

type ActionType = 'promote' | 'demote' | 'deactivate';

const columns = [
  { headerName: 'Email', field: 'email', headerCustomStyles: { width: '50%' } },
  { headerName: 'Role', field: 'role', headerCustomStyles: { width: '25%' } },
  { headerName: 'Actions', field: 'actions', headerCustomStyles: { width: '25%' } },
];

export const CompanyPage: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const [isDisasterEnabled, setIsDisasterEnabled] = useState(false);
  const [newUserValue, setNewUserValue] = useState('');
  const [showSnackbarMsg, setShowSnackbarMsg] = useState<SnackbarProps>({
    msg: '',
    type: 'success',
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserRole, setSelectedUserRole] = useState<string | null>(null);
  const [newUserFormError, setNewUserFormError] = useState('');

  const currentUser = useSelector((state: RootState) => state.user.currentUser);
  const companyMembers = useSelector((state: RootState) => state.admin.members);

  useEffect(() => {
    dispatch(getAdminMembers());
  }, []);

  const [showNewUser, setShowNewUser] = useState<boolean>(false);

  const onChangeNewUser = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewUserValue(e.target.value);
  };

  const onSubmitNewUser = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (newUserValue === '' || !emailRegex.test(newUserValue)) {
      setNewUserFormError('Provided value must be a valid email');
      return;
    }
    dispatch(inviteNewUser(newUserValue)).then((result) => {
      setShowNewUser(false);
      if (inviteNewUser.fulfilled.match(result)) {
        setShowSnackbarMsg({
          msg: 'Successfully invited new user',
          type: 'success',
        });
        dispatch(getAdminMembers());
        setNewUserFormError('');
      } else {
        setShowSnackbarMsg({
          msg: typeof result.payload === 'string' ? result.payload : 'Something went wrong',
          type: 'error',
        });
      }
    });
  };

  const handleMenuAction = (action: ActionType) => {
    if (!selectedUserId) return;
    const selectedUser = companyMembers.find((item) => item.id === selectedUserId);

    const actionMap: Record<ActionType, { dispatchAction: any; successMsg: string; errorMsg: string }> = {
      promote: {
        dispatchAction: promoteMember(selectedUserId),
        successMsg: `Successfully promoted: ${selectedUser?.email}`,
        errorMsg: `Something went wrong when promoting: ${selectedUser?.email}`,
      },
      demote: {
        dispatchAction: demoteMember(selectedUserId),
        successMsg: `Successfully demoted: ${selectedUser?.email}`,
        errorMsg: `Something went wrong when demoting:${selectedUser?.email}`,
      },
      deactivate: {
        dispatchAction: deactivateMember(selectedUserId),
        successMsg: `Successfully deactivated: ${selectedUser?.email}`,
        errorMsg: `Something went wrong when deactivating: ${selectedUser?.email}`,
      },
    };

    const selectedAction = actionMap[action];

    if (selectedAction) {
      dispatch(selectedAction.dispatchAction).then((result: any) => {
        const resolvedTypes = result.type.split('/');
        const resolvedType = resolvedTypes[resolvedTypes.length - 1];

        switch (resolvedType) {
          case 'fulfilled':
            if (action === 'demote' && selectedUserId === (currentUser as any).memberId) {
              dispatch(
                setCurrentUser({
                  user: {
                    ...currentUser,
                    teamRole: 'support',
                  },
                  accessToken: currentUser.accessToken,
                }),
              );
              dispatch(refreshToken());
            }
            setShowSnackbarMsg({ type: 'success', msg: selectedAction.successMsg });
            break;
          case 'rejected':
            setShowSnackbarMsg({ type: 'error', msg: selectedAction.errorMsg });
            break;
          default:
            return;
        }
      });
    }

    handleMenuClose();
  };

  const menuItems = (role: string) => {
    const items = [];

    if (role === 'admin') {
      items.push({
        label: 'Make Support',
        onClick: () => handleMenuAction('demote'),
      });
    }

    if (role === 'support') {
      items.push({
        label: 'Make Admin',
        onClick: () => handleMenuAction('promote'),
      });
    }

    items.push({
      label: 'Deactivate',
      onClick: () => handleMenuAction('deactivate'),
      disabled: role === 'admin',
      tooltip: role === 'admin' ? 'Change status to Support to enable deactivation' : '',
    });

    return items;
  };

  const renderCell = (row: any, column: Column) => {
    switch (column.field) {
      case 'actions':
        return renderAction(row.status, row.id, row.role);
      case 'role':
        return `${row['role'][0].toUpperCase()}${row['role'].substring(1)}`;
      default:
        return row[column.field];
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, userId: number, role: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedUserId(userId);
    setSelectedUserRole(role);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUserId(null);
    setSelectedUserRole(null);
  };

  const renderAction = (status: 'joined' | 'invited', id: number, role: string) => {
    switch (status) {
      case 'invited':
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              sx={{
                textTransform: 'none',
                width: '16.5625rem',
                borderRadius: '0rem',
                backgroundColor: '#e3e3e4',
              }}
              disabled={true}
              type="secondary"
            >
              Invite Sent
            </Button>
          </div>
        );
      case 'joined':
        const isOnlyAdmin =
          companyMembers.filter((member) => member.role === 'admin').length === 1 &&
          companyMembers.find((user) => user.id === id)?.role === 'admin';
        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              sx={{
                color: '#273194',
                textTransform: 'none',
                width: '16.5625rem',
                maxHeight: '2.5rem',
                border: '0rem',
              }}
              onClick={(event) => handleMenuOpen(event, id, role)}
              type="outline"
              disabled={isOnlyAdmin}
            >
              Edit Status
            </Button>
          </div>
        );
    }
  };

  useEffect(() => {
    dispatch(getActiveBanner()).then((result) => {
      if (result.payload && result.payload.activeNotification === 'disaster') {
        setIsDisasterEnabled(true);
      }
    });
  }, []);

  const handleToggleDisasterBanner = () => {
    setIsDisasterEnabled(!isDisasterEnabled);
    dispatch(updateAdminConfig({ isDisasterEnabled: !isDisasterEnabled })).then((result) => {
      if (updateAdminConfig.fulfilled.match(result)) {
        dispatch(setBannerStatus(!isDisasterEnabled ? 'disaster' : null))
      }
    });
  };

  const onAddNewUserClose = () => {
    setNewUserValue('');
    setNewUserFormError('');
    setShowNewUser(false);
  };

  return (
    <Box display="flex" flexDirection="row" height={'100vh'}>
      <StyledMain open={true}>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem',
          }}
        >
          <Typography sx={{ fontFamily: 'ABC Arizona Mix', fontWeight: 300, fontSize: '2.8125rem', lineHeight: '3.25rem' }}>
            Company
          </Typography>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Divider />
          <Box sx={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <Typography sx={{ fontSize: '2rem', lineHeight: '2.5rem', fontWeight: 400 }}>Members</Typography>
                <Typography sx={{ fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 400 }}>
                  {companyMembers.length} {companyMembers.length < 2 ? 'member' : 'members'} in
                  organization.
                </Typography>
              </Box>
              <Box sx={{ width: '18.75rem' }}>
                <Button type="secondary" onClick={() => setShowNewUser(true)}>
                  <AddOutlinedIcon />
                  <Spacer w={8} />
                  Add Member
                </Button>
              </Box>
            </Box>
            <BkTable
              data={companyMembers}
              columns={columns}
              renderCell={renderCell}
              initialRowsPerPage={50}
              renderPagination={false}
            />
            <LabeledCheckbox
              label="Show Disaster Banner"
              checked={isDisasterEnabled}
              onChange={handleToggleDisasterBanner}
              tooltip={<BkToolTip Icon={InfoOutlinedIcon} title="Shows disaster banner for all customers" />}
            />
          </Box>
        </Box>
      </StyledMain>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {selectedUserRole &&
          menuItems(selectedUserRole).map((item) => (
            <span key={item.label} style={{ display: 'flex', alignItems: 'center' }}>
              <MenuItem disabled={item.disabled} onClick={item.onClick}>
                {item.label}
              </MenuItem>
              {item.tooltip && (
                <BkToolTip
                  title={item.disabled && item.tooltip ? item.tooltip : ''}
                  Icon={HelpOutlineIcon}
                  sx={{ margin: '0.5rem' }}
                />
              )}
            </span>
          ))}
      </Menu>
      <AddNewAdminUser
        open={showNewUser}
        onClose={onAddNewUserClose}
        onChange={onChangeNewUser}
        error={newUserFormError}
        onSubmit={onSubmitNewUser}
        value={newUserValue}
      />
      <BkSnackBar
        open={!!showSnackbarMsg.msg}
        handleCloseSnackbar={() => setShowSnackbarMsg({ type: 'success', msg: '' })}
        snackMessage={showSnackbarMsg.msg}
        severity={showSnackbarMsg.type}
      />
    </Box>
  );
};
